import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { ResponsiveScatterPlot } from "@nivo/scatterplot";

function getInitials(name) {
  if (!name) return "";
  const words = name.trim().split(" ");
  if (words.length === 1) return name.substring(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function computeLinearRegression(points) {
  const n = points.length;
  if (n < 2) return null;
  const sumX = points.reduce((s, p) => s + p.x, 0);
  const sumY = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0);
  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return null;
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
}

const NODE_SIZE = 34;

function CustomNode({ node }) {
  const r = NODE_SIZE / 2;
  const { name, nodeColor } = node.data;
  const initials = getInitials(name);

  return (
    <g transform={`translate(${node.x}, ${node.y})`}>
      <circle
        r={r}
        cx={0}
        cy={0}
        fill={nodeColor}
        fillOpacity={0.15}
        stroke={nodeColor}
        strokeWidth={2}
      />
      <text
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={r * 0.6}
        fill={nodeColor}
        fontWeight="700"
      >
        {initials}
      </text>
    </g>
  );
}

export default function GhostDeerIndex({
  rows,
  theme,
  disableContainer = false
}) {
  const isDark = theme.palette.mode === "dark";

  const safeRows = rows || [];

  const avgWinRate =
    safeRows.length > 0
      ? safeRows.reduce((sum, r) => sum + r.stats.winRate, 0) / safeRows.length
      : 50;

  const getNodeColor = (winRate) => {
    if (Math.abs(winRate - avgWinRate) < 1)
      return isDark ? "#9ca3af" : "#6b7280";
    if (winRate > avgWinRate) return isDark ? "#4ade80" : "#16a34a";
    return isDark ? "#f87171" : "#dc2626";
  };

  const allPoints = safeRows.map((row) => ({
    x: row.stats.winRate,
    y: row.stats.games
  }));

  const regression = computeLinearRegression(allPoints);

  const scatterData = safeRows.map((row) => ({
    id: row.faction.name,
    data: [
      {
        x: row.stats.winRate,
        y: row.stats.games,
        name: row.faction.name,
        nodeColor: getNodeColor(row.stats.winRate)
      }
    ]
  }));

  const winRates = allPoints.map((p) => p.x);
  const games = allPoints.map((p) => p.y);
  const xPadding = 1;
  const xMin =
    winRates.length > 0 ? Math.max(0, Math.min(...winRates) - xPadding) : 0;
  const xMax =
    winRates.length > 0 ? Math.min(100, Math.max(...winRates) + xPadding) : 100;
  const yStep =
    games.length > 0
      ? Math.max(...games) > 30000
        ? 10000
        : Math.max(...games) > 3000
          ? 1000
          : Math.max(...games) > 300
            ? 500
            : Math.max(...games) > 100
              ? 100
              : Math.max(...games) > 50
                ? 10
                : 5
      : 500;
  const yMax =
    games.length > 0
      ? Math.ceil((Math.max(...games) * 1.15) / yStep) * yStep
      : 1000;

  const trendLineLayer = useMemo(
    () =>
      ({ xScale, yScale }) => {
        if (!regression || winRates.length < 2) return null;
        const xMinVal = Math.min(...winRates);
        const xMaxVal = Math.max(...winRates);
        return (
          <line
            x1={xScale(xMinVal)}
            y1={yScale(regression.slope * xMinVal + regression.intercept)}
            x2={xScale(xMaxVal)}
            y2={yScale(regression.slope * xMaxVal + regression.intercept)}
            stroke={isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)"}
            strokeWidth={2}
            strokeLinecap="round"
          />
        );
      },
    [regression, winRates, isDark]
  );

  const layers = useMemo(
    () => ["grid", "axes", trendLineLayer, "nodes", "mesh"],
    [trendLineLayer]
  );

  if (safeRows.length === 0) return null;

  const nivoTheme = {
    background: "transparent",
    axis: {
      domain: {
        line: {
          stroke: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)",
          strokeWidth: 1
        }
      },
      ticks: {
        line: {
          stroke: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
          strokeWidth: 1
        },
        text: {
          fill: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.5)",
          fontSize: 11
        }
      },
      legend: {
        text: {
          fill: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
          fontSize: 11,
          fontWeight: 600
        }
      }
    },
    grid: {
      line: {
        stroke: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
        strokeWidth: 1
      }
    }
  };

  const innerContent = (
    <Box sx={{ p: 2 }}>
      <Box sx={{ height: 480 }}>
        <ResponsiveScatterPlot
          data={scatterData}
          theme={nivoTheme}
          margin={{ top: 20, right: 30, bottom: 60, left: 70 }}
          xScale={{ type: "linear", min: xMin, max: xMax }}
          yScale={{ type: "linear", min: 0, max: yMax }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "Win Rate",
            legendOffset: 46,
            legendPosition: "middle",
            format: (v) => `${v}%`
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "Games",
            legendOffset: -56,
            legendPosition: "middle"
          }}
          nodeSize={NODE_SIZE}
          colors={{ scheme: "nivo" }}
          nodeComponent={CustomNode}
          tooltip={({ node }) => (
            <Box
              sx={{
                backgroundColor: isDark ? "#1e2a3a" : "#fff",
                color: theme.palette.text.primary,
                p: 1.5,
                borderRadius: 1,
                boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                minWidth: 140
              }}
            >
              <Typography
                sx={{ fontWeight: 700, mb: 0.5, fontSize: "0.85rem" }}
              >
                {node.data.name}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  fontSize: "0.8rem"
                }}
              >
                <Box sx={{ color: theme.palette.text.secondary }}>Win Rate</Box>
                <Box sx={{ fontWeight: 600 }}>{node.data.x}%</Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  fontSize: "0.8rem"
                }}
              >
                <Box sx={{ color: theme.palette.text.secondary }}>Games</Box>
                <Box sx={{ fontWeight: 600 }}>
                  {node.data.y.toLocaleString()}
                </Box>
              </Box>
            </Box>
          )}
          layers={layers}
          useMesh={true}
          enableGridX={true}
          enableGridY={true}
        />
      </Box>
      {/* <Typography
        sx={{
          textAlign: "center",
          color: isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)",
          fontSize: "0.72rem",
          fontStyle: "italic",
          mt: 1
        }}
      >
        Leaders are shown if they have a significant number of games played and
        their win rate differs from their parent faction by more than 5%.
      </Typography> */}
    </Box>
  );

  if (disableContainer) return innerContent;

  return (
    <Box
      sx={{
        backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
        borderRadius: 1,
        boxShadow: isDark
          ? "0 2px 8px rgba(0,0,0,0.3)"
          : "0 2px 8px rgba(0,0,0,0.1)"
      }}
    >
      {innerContent}
    </Box>
  );
}
